import { useEffect, useRef, useState } from "react";
import { Loader2, Send, Undo2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CopyReviewChanges, type CopyReviewActionRow } from "@/components/CopyReviewChanges";
import LlmReceiveProgress from "@/components/LlmReceiveProgress";
import SaveOperatorInsightButton from "@/components/operator/SaveOperatorInsightButton";
import type { CarouselLlmProgress } from "@/lib/api";
import { cn } from "@/lib/utils";

export type CopyChatMessage = {
  role?: string;
  text?: string;
  at?: string;
  kind?: string;
  patches?: { slide?: number; text?: string; sub?: string }[];
};

type Props = {
  messages: CopyChatMessage[];
  pendingActions?: CopyReviewActionRow[];
  itemId?: string;
  busy?: boolean;
  canUndo?: boolean;
  disabled?: boolean;
  error?: string;
  applyPending?: boolean;
  dismissPending?: boolean;
  onSend: (message: string) => void;
  onReviewRoles: () => void;
  onUndo: () => void;
  onApplyPending: (actionIds: string[], rejectIds: string[]) => void;
  onDismissPending: (actionId?: string) => void;
  reviewPending?: boolean;
  undoPending?: boolean;
  llmProgress?: CarouselLlmProgress | null;
  llmStatus?: string | null;
};

export default function CopyChatPanel({
  messages,
  pendingActions = [],
  itemId,
  busy,
  canUndo,
  disabled,
  error,
  applyPending,
  dismissPending,
  onSend,
  onReviewRoles,
  onUndo,
  onApplyPending,
  onDismissPending,
  reviewPending,
  undoPending,
  llmProgress,
  llmStatus,
}: Props) {
  const [draft, setDraft] = useState("");
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const hasPending = pendingActions.length > 0;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, busy, llmProgress?.percent, llmProgress?.phase]);

  const submit = () => {
    const text = draft.trim();
    if (!text || busy || disabled) return;
    onSend(text);
    setDraft("");
  };

  return (
    <div
      className={cn(
        "grid min-h-[22rem] overflow-hidden rounded-md border bg-white shadow-sm md:grid-cols-2",
        hasPending ? "border-violet-200" : "border-slate-200",
      )}
    >
      <div className="flex min-h-[22rem] min-w-0 flex-col border-b border-border md:border-b-0 md:border-r">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
          <div>
            <div className="text-sm font-medium text-slate-900">Czat o treści</div>
            <p className="text-[11px] text-muted-foreground">
              Ustal zmianę w rozmowie. Propozycje trafiają do prawej kolumny przed zapisem na slajdy.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <SaveOperatorInsightButton
              surface="carousel"
              sourceRef={itemId || ""}
              messages={messages.map((m) => ({
                role: String(m.role || "user"),
                content: String(m.text || ""),
              }))}
              disabled={disabled || !messages.length}
              className="h-8"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled || busy || reviewPending}
              onClick={onReviewRoles}
            >
              {reviewPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Users className="mr-1.5 h-3.5 w-3.5" />
              )}
              Review ról
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={disabled || busy || !canUndo || undoPending}
              onClick={onUndo}
            >
              {undoPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Undo2 className="mr-1.5 h-3.5 w-3.5" />
              )}
              Cofnij wdrożone
            </Button>
          </div>
        </div>

        <div className="flex-1 space-y-2 overflow-y-auto px-3 py-3">
          {messages.length === 0 && !busy ? (
            <p className="text-sm text-muted-foreground">
              Np. „slajd 2 za suchy, dodaj ciało” albo „hak ma być o otwieraniu piwa”. Najpierw ustal, potem zatwierdź po prawej.
            </p>
          ) : null}
          {messages.map((msg, i) => {
            const mine = (msg.role || "") === "user";
            return (
              <div
                key={`${msg.at || i}-${msg.role || "x"}`}
                className={cn("flex", mine ? "justify-end" : "justify-start")}
              >
                <div
                  className={cn(
                    "max-w-[92%] rounded-md px-3 py-2 text-sm whitespace-pre-wrap",
                    mine ? "bg-slate-900 text-white" : "bg-muted/50 text-foreground",
                    msg.kind === "roles_review" && "border border-violet-200 bg-violet-50/80",
                  )}
                >
                  {!mine && msg.kind === "roles_review" ? (
                    <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-violet-900">
                      Review ról
                    </div>
                  ) : null}
                  {msg.text}
                </div>
              </div>
            );
          })}
          {busy ? (
            <LlmReceiveProgress
              className="mt-1"
              progress={llmProgress}
              llmStatus={llmStatus || undefined}
              fallbackStartedAt={llmProgress?.started_at}
            />
          ) : null}
          <div ref={bottomRef} />
        </div>

        {error ? <p className="px-3 pb-1 text-xs text-destructive">{error}</p> : null}

        <div className="flex gap-2 border-t border-border p-3">
          <textarea
            className="min-h-[64px] flex-1 resize-y rounded-md border border-input bg-transparent px-2 py-1.5 text-sm"
            placeholder="Napisz, co zmienić na której karcie…"
            value={draft}
            disabled={disabled || busy}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <Button type="button" size="icon" disabled={disabled || busy || !draft.trim()} onClick={submit}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <aside className="flex min-h-[22rem] min-w-0 flex-col bg-slate-50/60">
        <div className="border-b border-border px-3 py-2">
          <div className="text-sm font-medium text-slate-900">Zmiany do zatwierdzenia</div>
          <p className="text-[11px] text-muted-foreground">
            Podgląd przed zapisem na slajdy. Nic nie wchodzi do drabinki bez Wdróż.
          </p>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {hasPending ? (
            <div className="space-y-3">
              <CopyReviewChanges
                actions={pendingActions}
                itemId={itemId}
                disabled={disabled || busy}
                pending={Boolean(applyPending || dismissPending)}
                onApply={(ids, rejectIds) => {
                  if (!ids.length) {
                    onDismissPending();
                    return;
                  }
                  onApplyPending(ids, rejectIds);
                }}
                onReject={(id) => onDismissPending(id)}
                compact
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={disabled || busy || dismissPending || applyPending}
                onClick={() => onDismissPending()}
              >
                {dismissPending ? "Odrzucam…" : "Odrzuć całą propozycję"}
              </Button>
            </div>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">
              Tu pojawi się diff haseł i opisów po wiadomości w czacie. Nic jeszcze nie czeka na akceptację.
            </p>
          )}
        </div>
      </aside>
    </div>
  );
}
